<template>
    <stat-card :expandable="expandable" :expanded="expanded" @toggle:expand="$emit('toggle:expand')">
        <template #header>
            <segmented-tabs class="sess-views" :options="viewOptions" :value="view" @change="view = $event" />
        </template>

        <div v-if="view === 'cal'" class="cal-view">
            <div class="cal-nav">
                <v-btn icon x-small class="cal-arrow" @click="changeMonth(-1)">
                    <v-icon size="16">mdi-chevron-left</v-icon>
                </v-btn>
                <span class="cal-month">{{ monthName }}</span>
                <v-btn icon x-small class="cal-arrow" @click="changeMonth(1)">
                    <v-icon size="16">mdi-chevron-right</v-icon>
                </v-btn>
            </div>
            <div class="cal-head">
                <div v-for="(label, i) in weekDays" :key="i" class="cal-hd">{{ label }}</div>
            </div>
            <div class="cal-grid">
                <div v-for="(cell, i) in cells" :key="i" class="cal-cell"
                    :class="{ empty: !cell, today: cell && isToday(cell.date) }">
                    <template v-if="cell">
                        <span>{{ cell.day }}</span>
                        <span v-if="statusByDate[cell.date]" class="cal-dot" :class="statusByDate[cell.date]" />
                    </template>
                </div>
            </div>
            <div class="sess-legend">
                <span class="sess-leg"><span class="cal-dot ok" />{{ $t('common.completeds') }}</span>
                <span class="sess-leg"><span class="cal-dot miss" />{{ $t('common.no_completeds') }}</span>
            </div>
        </div>

        <div v-else-if="view === 'sessions'" class="sess-view">
            <div class="sess-donut">
                <doughnut-chart :labels="donutLabels" :datasets="donutDatasets" :center-lines="donutCenter"
                    :styles="donutStyles" />
            </div>
            <div class="sess-legend">
                <span class="sess-leg"><span class="cal-dot ok" />{{ completedCount }} {{ $t('common.completeds') }}</span>
                <span class="sess-leg"><span class="cal-dot miss" />{{ notCompletedCount }} {{ $t('common.no_completeds') }}</span>
            </div>
        </div>

        <div v-else class="distrib-view">
            <div class="distrib-title">{{ $t('statistics.distribution') }}</div>
            <template v-if="distribTotal > 0">
                <div v-for="row in distribBars" :key="row.key" class="distrib-row">
                    <div class="distrib-label-row">
                        <span :style="{ color: row.color }">{{ row.label }}</span>
                        <span class="distrib-pct">{{ row.pct }}%</span>
                    </div>
                    <div class="distrib-track">
                        <div class="distrib-fill" :style="{ width: row.pct + '%', background: row.color }" />
                    </div>
                </div>
            </template>
            <div v-else class="sess-empty">{{ $t('common.without_data') }}</div>
        </div>
    </stat-card>
</template>

<script>
import DoughnutChart from '../common/DoughnutChart.vue'
import StatCard from './StatCard.vue'
import { expandableCardMixin } from '@/mixins/expandableCard';
import SegmentedTabs from '../common/SegmentedTabs.vue'

const COLORS = {
    ok: '#009966',
    miss: '#E85C5C',
    strength: '#339999',
    resistance: '#009966',
    speed: '#CCFF33',
}

export default {
    components: { DoughnutChart, StatCard, SegmentedTabs },
    mixins: [expandableCardMixin],
    name: "SessionsChart",
    props: {
        docs: {
            type: Array,
            default: null,
        },
        date: {
            type: String,
            default: null,
        },
    },
    data(vm) {
        return {
            view: 'cal',
            displayMonth: vm.$moment(vm.date || undefined).startOf('month'),
            donutStyles: { position: 'relative', height: '100%', width: '100%' },
            viewOptions: [
                { name: vm.$t('common.calendar'), value: 'cal' },
                { name: vm.$t('common.sessions'), value: 'sessions' },
                { name: vm.$t('statistics.distribution'), value: 'distrib' },
            ],
        }
    },
    computed: {
        currentDocs(vm = this) {
            return vm.docs || []
        },
        today(vm = this) {
            return vm.$moment().format('YYYY-MM-DD')
        },
        monthName(vm = this) {
            return vm.displayMonth.format('MMMM YYYY')
        },
        weekDays(vm = this) {
            const start = vm.$moment().startOf('isoWeek')
            return Array.from({ length: 7 }, (_, i) => vm.weekdayLetter(start.clone().add(i, 'days')))
        },
        cells(vm = this) {
            const startOfMonth = vm.displayMonth.clone().startOf('month')
            const daysInMonth = vm.displayMonth.daysInMonth()
            const leading = startOfMonth.isoWeekday() - 1
            const arr = []
            for (let i = 0; i < leading; i++) arr.push(null)
            for (let d = 1; d <= daysInMonth; d++) {
                arr.push({ day: d, date: startOfMonth.clone().date(d).format('YYYY-MM-DD') })
            }
            while (arr.length % 7 !== 0) arr.push(null)
            return arr
        },
        statusByDate(vm = this) {
            const map = {}
            vm.currentDocs.forEach(doc => {
                const date = doc?.rawDate?.full
                if (!date) return
                if (doc?.advance?.post) map[date] = 'ok'
                else if (!map[date]) map[date] = 'miss'
            })
            return map
        },
        completedCount(vm = this) {
            return vm.currentDocs.filter(doc => doc?.advance?.post).length
        },
        notCompletedCount(vm = this) {
            return vm.currentDocs.filter(doc => !doc?.advance?.post).length
        },
        totalSessions(vm = this) {
            return vm.currentDocs.length
        },
        donutLabels(vm = this) {
            return [vm.$t('common.completeds'), vm.$t('common.no_completeds')]
        },
        donutDatasets(vm = this) {
            return [{
                data: [vm.completedCount, vm.notCompletedCount],
                backgroundColor: [COLORS.ok, COLORS.miss],
                borderWidth: 0,
            }]
        },
        donutCenter(vm = this) {
            const theme = vm.$vuetify.theme.currentTheme
            return [
                { text: String(vm.totalSessions), fontSize: 26, fontWeight: 800, color: theme.txtPrimary },
                { text: vm.$t('common.sessions'), fontSize: 10, fontWeight: 500, color: theme.txtMuted },
            ]
        },
        distribution(vm = this) {
            let strength = 0, speed = 0, resistance = 0
            vm.currentDocs.forEach(doc => doc?.blocks?.forEach(block => block.exercises.forEach(exercise => {
                if (exercise.type === 'rest') return
                const sets = Number(exercise.config?.sets?.length || 0)
                const cat = vm.categoryKey(exercise.categories?.[0])
                if (cat === vm.CATEGORY.STRONG) strength += sets
                else if (cat === vm.CATEGORY.SPEED) speed += sets
                else if (cat === vm.CATEGORY.RESISTANCE) resistance += sets
            })))
            return { strength, speed, resistance }
        },
        distribTotal(vm = this) {
            return vm.distribution.strength + vm.distribution.speed + vm.distribution.resistance
        },
        distribBars(vm = this) {
            const total = vm.distribTotal || 1
            return [
                { key: 'strength', label: vm.$t('statistics.strength'), color: COLORS.strength, pct: Math.round(vm.distribution.strength / total * 100) },
                { key: 'resistance', label: vm.$t('statistics.resistance'), color: COLORS.resistance, pct: Math.round(vm.distribution.resistance / total * 100) },
                { key: 'speed', label: vm.$t('statistics.speed'), color: COLORS.speed, pct: Math.round(vm.distribution.speed / total * 100) },
            ].filter(row => row.pct > 0)
        },
    },
    methods: {
        changeMonth(delta) {
            this.displayMonth = this.displayMonth.clone().add(delta, 'month')
        },
        isToday(date) {
            return date === this.today
        },
    },
    watch: {
        date(val) {
            if (val) this.displayMonth = this.$moment(val).startOf('month')
        },
    }
}
</script>

<style scoped>
.sess-views {
    flex: 1;
}

.cal-view {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.cal-nav {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-bottom: 8px;
    flex-shrink: 0;
}

.cal-month {
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
    min-width: 110px;
    text-align: center;
    text-transform: capitalize;
}

.cal-arrow {
    width: 22px !important;
    height: 22px !important;
    border-radius: 5px !important;
}

.cal-arrow ::v-deep .v-icon {
    color: var(--v-txtMuted-base);
}

.cal-arrow:hover {
    background: var(--v-surface2-base) !important;
}

.cal-head {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    margin-bottom: 2px;
    flex-shrink: 0;
}

.cal-hd {
    font-size: 9px;
    color: var(--v-txtMuted-base);
    text-align: center;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.cal-grid {
    flex: 1;
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    grid-auto-rows: 1fr;
    gap: 2px;
    min-height: 0;
}

.cal-cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    font-size: 11px;
    color: var(--v-txtSecondary-base);
    border-radius: 6px;
    position: relative;
}

.cal-cell.empty {
    visibility: hidden;
}

.cal-cell.today {
    background: var(--v-surface2-base);
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.cal-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
}

.cal-dot.ok {
    background: #009966;
}

.cal-dot.miss {
    background: #E85C5C;
}

.sess-legend {
    display: flex;
    justify-content: center;
    gap: 16px;
    margin-top: 10px;
    flex-shrink: 0;
}

.sess-leg {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 10px;
    font-weight: 500;
    color: var(--v-txtMuted-base);
}

.sess-view {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 0;
}

.sess-donut {
    position: relative;
    height: 150px;
    flex-shrink: 0;
}

.distrib-view {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 12px;
}

.distrib-title {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--v-txtMuted-base);
    flex-shrink: 0;
}

.distrib-row {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.distrib-label-row {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    font-weight: 600;
}

.distrib-pct {
    color: var(--v-txtSecondary-base);
}

.distrib-track {
    height: 10px;
    background: var(--v-surface2-base);
    border-radius: 5px;
    overflow: hidden;
}

.distrib-fill {
    height: 100%;
    border-radius: 5px;
}

.sess-empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    color: var(--v-txtMuted-base);
}
</style>
